import { useEffect, useMemo, useState } from 'react'
import {
  deleteProgramOverride,
  listPrograms,
  upsertProgram,
  type Program,
} from '../lib/api'

type Draft = { balance: string; cpp: string }

function toDraft(p: Program): Draft {
  return {
    balance: String(p.balance),
    cpp: p.override_cpp != null ? String(p.override_cpp) : '',
  }
}

function isDirty(p: Program, d: Draft): boolean {
  const balDirty = String(p.balance) !== d.balance.trim()
  const overrideStr = p.override_cpp != null ? String(p.override_cpp) : ''
  const cppDirty = overrideStr !== d.cpp.trim()
  return balDirty || cppDirty
}

export function Programs() {
  const [programs, setPrograms] = useState<Program[] | null>(null)
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  const reload = () =>
    listPrograms()
      .then((r) => {
        setPrograms(r.programs)
        setDrafts(Object.fromEntries(r.programs.map((p) => [p.program, toDraft(p)])))
      })
      .catch((e) => setErr(e?.message || 'failed'))

  useEffect(() => {
    reload()
  }, [])

  const setField = (program: string, field: keyof Draft, value: string) => {
    setDrafts((d) => ({ ...d, [program]: { ...d[program], [field]: value } }))
  }

  const save = async (p: Program) => {
    setErr(null)
    setInfo(null)
    setSavingKey(p.program)
    try {
      const d = drafts[p.program]
      const balance = parseInt(d.balance, 10)
      if (Number.isNaN(balance) || balance < 0) {
        throw new Error('balance must be a non-negative integer')
      }
      const cppRaw = d.cpp.trim()
      let cpp: number | null = null
      if (cppRaw !== '') {
        cpp = parseFloat(cppRaw)
        if (Number.isNaN(cpp) || cpp < 0) throw new Error('cpp must be a non-negative number')
      }
      await upsertProgram(p.program, { balance, transfer_value_cpp: cpp })
      setInfo(`saved ${p.program}.`)
      await reload()
    } catch (e: any) {
      setErr(e?.message || 'save failed')
    } finally {
      setSavingKey(null)
    }
  }

  const clearOverride = async (p: Program) => {
    setErr(null)
    setInfo(null)
    setSavingKey(p.program)
    try {
      await deleteProgramOverride(p.program)
      setInfo(`cleared override + balance for ${p.program}.`)
      await reload()
    } catch (e: any) {
      setErr(e?.message || 'clear failed')
    } finally {
      setSavingKey(null)
    }
  }

  const totalDollars = useMemo(() => {
    if (!programs) return 0
    return programs.reduce((sum, p) => sum + p.balance * (p.effective_cpp / 100), 0)
  }, [programs])

  if (!programs) {
    return <div className="text-zinc-500 text-sm">loading…</div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-light text-zinc-100">programs</h1>
        <p className="text-[12px] text-zinc-500 mt-1 max-w-2xl">
          balances + per-program transfer value (cents-per-point). overrides win over the global TPG fallback;
          delete an override to fall back to TPG. recommend re-reads these on every call, so edits are live.
        </p>
      </div>

      {err && <div className="text-red-400 text-sm">{err}</div>}
      {info && <div className="text-emerald-500 text-sm">{info}</div>}

      <div className="text-[11px] uppercase tracking-widest text-zinc-500">
        portfolio value · ${totalDollars.toFixed(2).toLocaleString()}{' '}
        <span className="normal-case tracking-normal text-zinc-700">
          (Σ balance × cpp)
        </span>
      </div>

      <div className="border border-zinc-900 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-zinc-500 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="px-4 py-2 text-left font-normal">program</th>
              <th className="px-4 py-2 text-right font-normal">balance</th>
              <th className="px-4 py-2 text-right font-normal">cpp</th>
              <th className="px-4 py-2 text-right font-normal">value</th>
              <th className="px-4 py-2 text-right font-normal" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900">
            {programs.map((p) => {
              const d = drafts[p.program] ?? toDraft(p)
              const dirty = isDirty(p, d)
              const saving = savingKey === p.program
              const value = p.balance * (p.effective_cpp / 100)
              return (
                <tr key={p.program} className="hover:bg-zinc-950/50">
                  <td className="px-4 py-3 align-middle">
                    <div className="text-zinc-200 uppercase">{p.program}</div>
                    <div className="text-[10px] text-zinc-600 mt-0.5">
                      {p.in_use_on_cards && <span className="mr-2">on a card</span>}
                      {p.source === 'override' && <span className="text-emerald-600">custom</span>}
                      {p.source === 'fallback' && (
                        <span>tpg fallback · {p.fallback_cpp}</span>
                      )}
                      {p.source === 'none' && <span className="text-zinc-700">no cpp set</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-middle text-right">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={d.balance}
                      onChange={(e) => setField(p.program, 'balance', e.target.value)}
                      className="w-32 bg-zinc-950 border border-zinc-900 rounded px-2 py-1 text-right text-zinc-200 text-sm focus:border-zinc-700 focus:outline-none"
                    />
                  </td>
                  <td className="px-4 py-3 align-middle text-right">
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      placeholder={p.fallback_cpp != null ? String(p.fallback_cpp) : '—'}
                      value={d.cpp}
                      onChange={(e) => setField(p.program, 'cpp', e.target.value)}
                      className="w-24 bg-zinc-950 border border-zinc-900 rounded px-2 py-1 text-right text-zinc-200 text-sm focus:border-zinc-700 focus:outline-none placeholder:text-zinc-700"
                    />
                  </td>
                  <td className="px-4 py-3 align-middle text-right text-zinc-400 tabular-nums">
                    ${value.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 align-middle text-right space-x-2 whitespace-nowrap">
                    <button
                      disabled={!dirty || saving}
                      onClick={() => save(p)}
                      className="px-3 py-1 rounded bg-zinc-100 text-zinc-900 text-xs font-medium hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {saving ? '…' : 'save'}
                    </button>
                    {p.source === 'override' && (
                      <button
                        disabled={saving}
                        onClick={() => clearOverride(p)}
                        title="remove this override + balance, fall back to TPG cpp"
                        className="px-3 py-1 rounded border border-zinc-800 text-zinc-400 text-xs hover:bg-zinc-900 disabled:opacity-30"
                      >
                        clear
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <AddProgramRow
        existing={new Set(programs.map((p) => p.program))}
        onAdded={reload}
        onError={setErr}
      />
    </div>
  )
}

function AddProgramRow({
  existing,
  onAdded,
  onError,
}: {
  existing: Set<string>
  onAdded: () => void
  onError: (e: string) => void
}) {
  const [program, setProgram] = useState('')
  const [balance, setBalance] = useState('')
  const [cpp, setCpp] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    onError('')
    const key = program.trim().toLowerCase()
    if (!key) return onError('program key required')
    if (existing.has(key)) return onError(`${key} already in the list above`)
    const bal = balance.trim() === '' ? 0 : parseInt(balance, 10)
    if (Number.isNaN(bal) || bal < 0) return onError('balance must be ≥ 0')
    let cppVal: number | null = null
    if (cpp.trim() !== '') {
      cppVal = parseFloat(cpp)
      if (Number.isNaN(cppVal) || cppVal < 0) return onError('cpp must be ≥ 0')
    }
    setBusy(true)
    try {
      await upsertProgram(key, { balance: bal, transfer_value_cpp: cppVal })
      setProgram('')
      setBalance('')
      setCpp('')
      onAdded()
    } catch (e: any) {
      onError(e?.message || 'add failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="border border-dashed border-zinc-900 rounded p-4 space-y-3">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500">add program</div>
      <div className="flex gap-2 items-center text-sm">
        <input
          placeholder="key (e.g. avios)"
          value={program}
          onChange={(e) => setProgram(e.target.value)}
          className="flex-1 bg-zinc-950 border border-zinc-900 rounded px-3 py-1.5 text-zinc-200 focus:border-zinc-700 focus:outline-none"
        />
        <input
          type="number"
          min={0}
          step={1}
          placeholder="balance"
          value={balance}
          onChange={(e) => setBalance(e.target.value)}
          className="w-32 bg-zinc-950 border border-zinc-900 rounded px-3 py-1.5 text-right text-zinc-200 focus:border-zinc-700 focus:outline-none"
        />
        <input
          type="number"
          min={0}
          step={0.01}
          placeholder="cpp"
          value={cpp}
          onChange={(e) => setCpp(e.target.value)}
          className="w-24 bg-zinc-950 border border-zinc-900 rounded px-3 py-1.5 text-right text-zinc-200 focus:border-zinc-700 focus:outline-none"
        />
        <button
          disabled={busy || !program.trim()}
          onClick={submit}
          className="px-3 py-1.5 rounded bg-zinc-100 text-zinc-900 text-xs font-medium hover:bg-white disabled:opacity-30"
        >
          {busy ? '…' : 'add'}
        </button>
      </div>
    </div>
  )
}
